/**
 * Auth context. Holds the in-memory access token + active session, and exposes login/logout. The
 * token lives only in memory; the API also sets an httpOnly refresh cookie, so on boot we try a
 * silent refresh to restore the workspace session across a page reload (Slice 2b). Platform sessions
 * (Slice 1b) are a separate, non-persisted mode used only for tenant creation.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiClient, type PlatformSession, type Session } from './api';

interface AuthState {
  api: ApiClient;
  session: Session | null;
  platform: PlatformSession | null;
  /** True while the boot-time silent refresh is in flight — avoids flashing the login screen. */
  restoring: boolean;
  signIn: (session: Session) => void;
  signInPlatform: (platform: PlatformSession) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const api = useMemo(() => new ApiClient(), []);
  const [session, setSession] = useState<Session | null>(null);
  const [platform, setPlatform] = useState<PlatformSession | null>(null);
  const [restoring, setRestoring] = useState(true);

  // A request whose access token expired and whose refresh cookie is also gone → drop the session
  // so the router falls back to the login screen (instead of stranding the user on a 401 error).
  useEffect(() => {
    api.setOnAuthExpired(() => {
      setSession(null);
      setPlatform(null);
    });
    return () => api.setOnAuthExpired(null);
  }, [api]);

  // Boot: attempt to restore a workspace session from the refresh cookie.
  useEffect(() => {
    let live = true;
    api
      .refresh()
      .then((s) => {
        if (!live) return;
        api.setToken(s.access);
        setSession(s);
      })
      .catch(() => {
        /* no valid refresh cookie — stay signed out */
      })
      .finally(() => live && setRestoring(false));
    return () => {
      live = false;
    };
  }, [api]);

  const value = useMemo<AuthState>(
    () => ({
      api,
      session,
      platform,
      restoring,
      signIn: (s) => {
        api.setToken(s.access);
        setSession(s);
        setPlatform(null);
      },
      signInPlatform: (p) => {
        api.setToken(p.access);
        setPlatform(p);
        setSession(null);
      },
      signOut: async () => {
        try {
          await api.logout();
        } catch {
          /* best effort — clear locally regardless */
        }
        api.setToken(null);
        setSession(null);
        setPlatform(null);
      },
    }),
    [api, session, platform, restoring],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
